import { WebinarApiData } from 'src/types/api/webinars';
import { WebinarData } from 'src/types/state/webinar';
import {
  WEBINAR_REGISTRATION_STATUS,
  JOIN_WINDOW_MINUTES,
  WebinarRegistrationStatus,
  DEFAULT_DURATION_MINUTES,
  FALLBACK_TIMEZONE,
  DATE_FORMAT,
} from 'src/constants/webinar';
import { DEFAULT_LOCALE } from 'src/constants/localization';
import { ClientLog } from 'src/constants/clientLog';
import ClientLogger from 'src/utils/logger';
import moment from 'moment-timezone';

export const getUserTimezone = (): string | null => {
  let detectedTimezone;
  try {
    detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return detectedTimezone;
  } catch (error) {
    ClientLogger.error(ClientLog.PayloadProcessingError, new Error('Failed to get user timezone'), {
      error,
      detectedTimezone: detectedTimezone || 'undefined',
    });
    return null;
  }
};

/**
 * Gets the user's locale from the HTML document with fallback to default locale
 * @returns User locale (e.g., 'en-US', 'ko-KR', 'de-DE') or DEFAULT_LOCALE as fallback
 */
export const getUserLocale = (): string => {
  try {
    const documentLocale = document?.documentElement?.lang;
    if (documentLocale) {
      return documentLocale;
    }

    return DEFAULT_LOCALE;
  } catch (error) {
    ClientLogger.error(ClientLog.PayloadProcessingError, new Error('Failed to get user locale'), {
      error,
    });
    return DEFAULT_LOCALE;
  }
};

/**
 * Converts timestamp from webinar timezone to UTC using moment-timezone
 * Note: The timestamp is in webinar timezone, not UTC despite the field name
 * @param timestampMs - Unix timestamp in milliseconds (in webinar timezone)
 * @param webinarTimezone - Source timezone of the timestamp
 * @returns Date object representing the time in UTC
 */
export const convertToUTC = (timestampMs: number, webinarTimezone: string): Date => {
  const momentInWebinarTz = moment.tz(timestampMs, webinarTimezone);
  const offsetMinutes = momentInWebinarTz.utcOffset();
  const utcTimestamp = timestampMs - (offsetMinutes * 60 * 1000);
  return new Date(utcTimestamp);
};

export const formatWebinarDate = (date: Date, targetTimezone: string): string => {
  try {
    const userLocale = getUserLocale();
    const options: Intl.DateTimeFormatOptions = {
      weekday: DATE_FORMAT.SHORT,
      year: DATE_FORMAT.NUMERIC,
      month: DATE_FORMAT.SHORT,
      day: DATE_FORMAT.NUMERIC,
      timeZone: targetTimezone,
    };
    return date.toLocaleDateString(userLocale, options);
  } catch (error) {
    ClientLogger.error(ClientLog.PayloadProcessingError, new Error('Failed to format webinar date'), {
      error,
      date: date.toString(),
      targetTimezone,
      userLocale: getUserLocale(),
    });
    return String(date);
  }
};

export const formatWebinarStartTime = (date: Date, targetTimezone: string): string => {
  try {
    const userLocale = getUserLocale();
    const options: Intl.DateTimeFormatOptions = {
      hour: DATE_FORMAT.NUMERIC,
      minute: DATE_FORMAT.TWO_DIGIT,
      timeZone: targetTimezone,
    };
    return date.toLocaleTimeString(userLocale, options);
  } catch (error) {
    ClientLogger.error(ClientLog.PayloadProcessingError, new Error('Failed to format webinar start time'), {
      error,
      date: date.toString(),
      targetTimezone,
      startTimeTimestamp: date.getTime(),
      userLocale: getUserLocale(),
    });
    return String(date);
  }
};

export const formatWebinarEndTime = (startDate: Date, durationMinutes: number, targetTimezone: string): string => {
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
  return formatWebinarStartTime(endDate, targetTimezone);
};

/**
 * Determines registration status based on backend data and current time
 * @param isRegistered - Whether user is registered from backend
 * @param webinarScheduledTimestamp - Unix timestamp of webinar start (in webinar timezone)
 * @param durationMinutes - Webinar duration in minutes
 * @param webinarTimezone - Webinar timezone for proper time comparison (required)
 * @returns Registration status according to LLD
 */
export const getWebinarRegistrationStatus = (
  isRegistered: boolean,
  webinarScheduledTimestamp: number,
  durationMinutes: number,
  webinarTimezone: string,
): WebinarRegistrationStatus => {
  if (!isRegistered) {
    return WEBINAR_REGISTRATION_STATUS.REGISTER;
  }

  const now = Date.now();

  // Convert webinar timestamp to UTC for proper comparison with Date.now()
  const webinarStartUTC = convertToUTC(webinarScheduledTimestamp, webinarTimezone).getTime();
  const webinarEndUTC = webinarStartUTC + (durationMinutes * 60 * 1000);
  const joinWindowStart = webinarStartUTC - (JOIN_WINDOW_MINUTES * 60 * 1000);

  if (now >= joinWindowStart && now <= webinarEndUTC) {
    return WEBINAR_REGISTRATION_STATUS.JOIN;
  }

  return WEBINAR_REGISTRATION_STATUS.REGISTERED;
};

/**
 * Checks if a timezone abbreviation is a generic GMT/offset format
 * @param abbreviation - Timezone abbreviation to check
 * @returns True if it's a generic format like GMT+1, GMT+5:30, -03, etc.
 */
const isGenericTimezoneFormat = (abbreviation: string): boolean => {
  return /^GMT[+-]\d{1,2}(:\d{2})?$|^[+-]\d{1,2}(:\d{2})?$/.test(abbreviation);
};

/**
 * Converts backend timezone format to frontend abbreviation using hybrid approach
 * @param backendTimezone - Backend timezone format (e.g., 'America/New_York')
 * @param referenceTime - Reference time to use for timezone abbreviation lookup (defaults to current time)
 * @returns Frontend timezone abbreviation (e.g., 'EST' or 'EDT')
 */
export const convertTimezoneToAbbreviation = (
  backendTimezone: string,
  referenceTime: Date = new Date(),
): string => {
  try {
    const userLocale = getUserLocale();
    const formatter = new Intl.DateTimeFormat(userLocale, {
      timeZone: backendTimezone,
      timeZoneName: DATE_FORMAT.SHORT,
    });

    const parts = formatter.formatToParts(referenceTime);
    const timeZoneName = parts.find((part) => part.type === 'timeZoneName');
    const intlResult = timeZoneName?.value;

    if (intlResult && !isGenericTimezoneFormat(intlResult)) {
      return intlResult;
    }

    // Fallback to moment-timezone for better abbreviations
    const momentAbbr = moment.tz(referenceTime, backendTimezone).format('z');

    if (momentAbbr && !isGenericTimezoneFormat(momentAbbr) && momentAbbr !== backendTimezone) {
      return momentAbbr;
    }

    return intlResult || backendTimezone;
  } catch (error) {
    ClientLogger.error(ClientLog.PayloadProcessingError, new Error('Failed to convert timezone to abbreviation'), {
      error,
      backendTimezone,
      referenceTime: referenceTime.toString(),
      userLocale: getUserLocale(),
    });
    return backendTimezone;
  }
};

/**
 * Transforms webinar API data to UI format with proper formatting and timezone handling
 * @param apiData - Raw webinar data from API
 * @param userTimezone - User's timezone (optional, will auto-detect if not provided)
 * @returns Transformed webinar data for UI consumption
 */
export const transformWebinarApiDataToUI = (
  apiData: WebinarApiData,
): WebinarData => {
  // Get target timezone with fallback chain: provided timezone -> user timezone -> webinar timezone -> UTC
  const targetTimezone = getUserTimezone() || apiData.webinarTimezone || FALLBACK_TIMEZONE;

  const webinarScheduledTimestamp = apiData.webinarScheduledUTCTimestamp * 1000;
  const duration = apiData.webinarDuration || DEFAULT_DURATION_MINUTES;

  const scheduledDate = convertToUTC(webinarScheduledTimestamp, apiData.webinarTimezone);

  const date = formatWebinarDate(scheduledDate, targetTimezone);
  const startTime = formatWebinarStartTime(scheduledDate, targetTimezone);
  const endTime = formatWebinarEndTime(scheduledDate, duration, targetTimezone);

  const registrationStatus = getWebinarRegistrationStatus(
    apiData.isRegistered,
    webinarScheduledTimestamp,
    duration,
    apiData.webinarTimezone,
  );

  return {
    webinarId: apiData.webinarId,
    title: apiData.title || '',
    description: apiData.description || '',
    date,
    startTime,
    endTime,
    webinarScheduledTimestamp,
    duration,
    registrationStatus,
    registrantURL: apiData.registrationUrl || '',
    userTimezone: convertTimezoneToAbbreviation(targetTimezone, scheduledDate),
  };
};

/**
 * Transforms an array of webinar API data to UI format
 * @param apiDataArray - Array of raw webinar data from API
 * @param userTimezone - User's timezone (optional, will auto-detect if not provided)
 * @returns Array of transformed webinar data for UI consumption
 */
export const transformWebinarApiArrayToUI = (
  apiDataArray: WebinarApiData[],
): WebinarData[] => {
  if (!apiDataArray?.length) {
    return [];
  }

  return apiDataArray.map((apiData) => transformWebinarApiDataToUI(apiData));
};
